import { ColumnFiltersState, OnChangeFn, PaginationState, SortingState, Updater } from '@tanstack/react-table';
import { isNil } from 'lodash';
import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import { FilterType } from '../../_openapi/portfolio-optimizer';
import { DEFAULT_PAGE_SIZE } from '../../constants';

export const usePersistentTableState = (columns: any[], defaultValues?: { sorting: SortingState }) => {
    const [searchParams, setSearchParams] = useSearchParams();

    const pagination = useMemo(() => {
        return {
            pageIndex: Number(searchParams.get('pageIndex')) || 0,
            pageSize: Number(searchParams.get('pageSize')) || DEFAULT_PAGE_SIZE,
        };
    }, [searchParams]);
    const setPagination: OnChangeFn<PaginationState> = useCallback(
        (updaterOrValue: Updater<PaginationState>) => {
            const updated: PaginationState =
                typeof updaterOrValue === 'function' ? updaterOrValue(pagination) : updaterOrValue;
            setSearchParams(
                (params) => {
                    if (params.get('pageSize') && params.get('pageSize') !== updated.pageSize.toString()) {
                        params.set('pageIndex', '0');
                    } else {
                        params.set('pageIndex', updated.pageIndex.toString());
                    }

                    params.set('pageSize', updated.pageSize.toString());

                    return params;
                },
                { replace: true },
            );
        },
        [pagination, setSearchParams],
    );

    const sorting = useMemo(() => {
        const sortCol = searchParams.get('sortCol');
        return sortCol ? [{ id: sortCol, desc: searchParams.get('sortDir') === 'desc' }] : defaultValues?.sorting;
    }, [defaultValues, searchParams]);

    const setSorting: OnChangeFn<SortingState> = useCallback(
        (updaterOrValue: Updater<SortingState>) => {
            const updated: SortingState =
                typeof updaterOrValue === 'function' ? updaterOrValue(sorting || []) : updaterOrValue;
            setSearchParams(
                (params) => {
                    if (updated.length) {
                        params.set('sortCol', updated[0].id);
                        params.set('sortDir', updated[0].desc ? 'desc' : 'asc');
                    } else {
                        params.delete('sortCol');
                        params.delete('sortDir');
                    }

                    return params;
                },
                { replace: true },
            );
        },
        [setSearchParams, sorting],
    );

    const columnFilters: ColumnFiltersState = useMemo(() => {
        return columns.reduce((result, { id, meta }) => {
            const value = getFilterValue(id, searchParams, meta?.filterType);
            return !isNil(value) ? [...result, { id, value }] : result;
        }, []);
    }, [columns, searchParams]);

    const setColumnFilters: OnChangeFn<ColumnFiltersState> = useCallback(
        (updaterOrValue: Updater<ColumnFiltersState>) => {
            const updated: ColumnFiltersState =
                typeof updaterOrValue === 'function' ? updaterOrValue(columnFilters) : updaterOrValue;

            const params = updated.reduce(
                (result, filter) => {
                    if (Array.isArray(filter.value) && !filter.value.length) {
                        return result;
                    }

                    return {
                        ...result,
                        [filter.id]: filter.value,
                    };
                },
                {
                    pageIndex: '0',
                } as { [k: string]: any },
            );

            if (searchParams.get('sortCol')) {
                params.sortCol = searchParams.get('sortCol');
            }

            if (searchParams.get('sortDir')) {
                params.sortDir = searchParams.get('sortDir');
            }

            if (searchParams.get('pageSize')) {
                params.pageSize = searchParams.get('pageSize');
            }

            if (searchParams.get('dataVariant')) {
                params.dataVariant = searchParams.get('dataVariant');
            }

            setSearchParams({ ...params }, { replace: true });
        },
        [columnFilters, searchParams, setSearchParams],
    );

    const resetTable = useCallback(() => {
        setSearchParams(undefined, { replace: true });
    }, [setSearchParams]);

    return {
        pagination,
        setPagination,
        sorting,
        setSorting,
        columnFilters,
        setColumnFilters,
        resetTable,
    };
};

const getFilterValue = (id: string, searchParams: URLSearchParams, filterType?: FilterType) => {
    if (!filterType || filterType === FilterType.STRING || filterType === FilterType.LIST) {
        const value = searchParams.getAll(id);
        return value.length ? value : undefined;
    }

    return searchParams.get(id) ?? undefined;
};
